import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Lock, User as UserIcon, AlertCircle, LogIn, ChevronDown, ChevronUp, KeyRound, ArrowLeft } from "lucide-react";
import { API_BASE } from "@/lib/api";

const LOGO_URL = "https://customer-assets-wrfwihn1.emergentagent.net/job_park-light-manager/artifacts/ljez3ua3_Senza%20titolo88.png";

function friendlyError(err) {
  // No response at all -> network / CORS / DNS / server unreachable
  if (!err?.response) {
    if (err?.code === "ECONNABORTED" || String(err?.message || "").toLowerCase().includes("timeout")) {
      return "Il server non ha risposto in tempo. Riprova o controlla la connessione.";
    }
    if (String(err?.message || "").toLowerCase().includes("network")) {
      return "Impossibile contattare il server. Controlla la connessione internet o riprova più tardi.";
    }
    return "Errore di connessione al server. Riprova tra qualche istante.";
  }
  const status = err.response.status;
  const detail = err.response.data?.detail;
  if (status === 401) return typeof detail === "string" ? detail : "Credenziali non valide.";
  if (status === 422) return "Dati non validi. Controlla username e password.";
  if (status === 429) return typeof detail === "string" ? detail : "Troppi tentativi. Riprova più tardi.";
  if (status >= 500) return `Errore del server (${status}). Riprova più tardi o contatta il supporto.`;
  return typeof detail === "string" ? detail : `Errore imprevisto (HTTP ${status}).`;
}

export default function Login() {
  const { login, verifyPin } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showFallback, setShowFallback] = useState(false);
  const [pinStep, setPinStep] = useState(null); // {pin_token, expires_in_seconds} | null
  const [pin, setPin] = useState("");
  const pinInputRef = useRef(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    if (pinStep && pinInputRef.current) {
      pinInputRef.current.focus();
    }
  }, [pinStep]);

  // If the OAuth callback returned with an error param, show it
  const oauthError = params.get("error");

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username || !password) {
      setError("Inserisci username e password.");
      return;
    }
    setLoading(true);
    try {
      const res = await login(username, password);
      if (res?.step === "pin_required") {
        // Move to PIN step
        setPinStep({ pin_token: res.pin_token, expires_in_seconds: res.expires_in_seconds });
        setPin("");
        return;
      }
      const from = params.get("from") || "/dashboard";
      navigate(from, { replace: true });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[Irollo CRM] Login failed:", {
        message: err?.message,
        code: err?.code,
        status: err?.response?.status,
        data: err?.response?.data,
        apiBase: API_BASE,
      });
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitPin = async (e) => {
    e.preventDefault();
    setError("");
    if (!pin || pin.length < 4) {
      setError("Inserisci il codice PIN a 6 cifre.");
      return;
    }
    setLoading(true);
    try {
      await verifyPin(pinStep.pin_token, pin);
      const from = params.get("from") || "/dashboard";
      navigate(from, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      // If challenge expired, kick user back to step 1
      if (status === 401 && err?.response?.data?.detail?.includes("scaduta")) {
        setPinStep(null);
        setPin("");
        setPassword("");
      }
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const backToPassword = () => {
    setPinStep(null);
    setPin("");
    setError("");
  };

  return (
    <div className="min-h-screen w-full bg-[#1C1917] relative overflow-hidden">
      {/* Warm ambient glow */}
      <div
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full opacity-25 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, #F56A1F 0%, transparent 60%)" }}
      />
      {/* Subtle grain */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <img
              src={LOGO_URL}
              alt="Irollo — Illumina & Arreda dal 1964"
              className="w-56 h-auto mb-4"
              style={{ filter: "invert(1) brightness(1.15) contrast(1.05)" }}
            />
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#F56A1F] font-semibold">
              Gestionale CRM · Accesso Riservato
            </div>
          </div>

          <Card className="border-stone-800 bg-stone-950/60 backdrop-blur-xl rounded-2xl p-7 shadow-2xl">
            <div className="mb-5">
              <h1 className="text-2xl font-semibold tracking-tight text-white mb-1">Bentornato</h1>
              <p className="text-stone-400 text-sm">Accedi al tuo gestionale clienti.</p>
            </div>

            {oauthError && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-950/40 border border-red-900/50 text-red-300 text-sm mb-4">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{oauthError}</span>
              </div>
            )}

            {/* Google Auth (primary) */}
            <Button
              data-testid="login-google"
              type="button"
              onClick={startGoogleLogin}
              className="w-full h-11 bg-white hover:bg-stone-100 text-stone-900 font-medium gap-3 mb-3">
              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.44c-.28 1.49-1.12 2.75-2.39 3.6v3h3.87c2.26-2.09 3.57-5.16 3.57-8.84z"/>
                <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.9l-3.87-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.08C3.27 21.31 7.31 24 12 24z"/>
                <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.63H1.29A11.94 11.94 0 000 12c0 1.94.47 3.78 1.29 5.37l3.98-3.08z"/>
                <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.27 2.69 1.29 6.63l3.98 3.08c.95-2.85 3.6-4.96 6.73-4.96z"/>
              </svg>
              Continua con Google
            </Button>

            <button
              data-testid="login-toggle-fallback"
              type="button"
              onClick={() => setShowFallback((v) => !v)}
              className="w-full flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-widest text-stone-500 hover:text-stone-300 py-2 transition-colors">
              Accesso amministratore
              {showFallback ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>

            {showFallback && !pinStep && (
            <form onSubmit={submit} className="space-y-4 pt-3 border-t border-stone-800">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                  Username
                </Label>
                <div className="relative mt-1.5">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                  <Input
                    data-testid="login-username"
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Irollosales"
                    className="pl-9 h-11 bg-stone-900 border-stone-800 text-white placeholder:text-stone-600 focus-visible:ring-1 focus-visible:ring-[#F56A1F] focus-visible:border-[#F56A1F]"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                  Password
                </Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                  <Input
                    data-testid="login-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    className="pl-9 h-11 bg-stone-900 border-stone-800 text-white placeholder:text-stone-600 focus-visible:ring-1 focus-visible:ring-[#F56A1F] focus-visible:border-[#F56A1F]"
                  />
                </div>
              </div>

              {error && (
                <div
                  data-testid="login-error"
                  className="flex items-start gap-2 p-3 rounded-lg bg-red-950/40 border border-red-900/50 text-red-300 text-sm">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#F56A1F] hover:bg-[#E05206] text-white font-medium gap-2 shadow-lg shadow-[#F56A1F]/20 mt-2">
                {loading ? "Accesso in corso…" : (<><LogIn className="w-4 h-4" /> Accedi</>)}
              </Button>
            </form>
            )}

            {showFallback && pinStep && (
            <form onSubmit={submitPin} className="space-y-4 pt-3 border-t border-stone-800">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-full bg-[#F56A1F]/15 border border-[#F56A1F]/30 flex items-center justify-center">
                  <KeyRound className="w-4 h-4 text-[#F56A1F]" />
                </div>
                <div className="min-w-0">
                  <div className="text-white text-sm font-medium">Verifica di sicurezza</div>
                  <div className="text-stone-500 text-xs">Inserisci il codice PIN a 6 cifre</div>
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                  Codice PIN
                </Label>
                <div className="relative mt-1.5">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                  <Input
                    data-testid="login-pin"
                    ref={pinInputRef}
                    type="password"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="••••••"
                    className="pl-9 h-11 bg-stone-900 border-stone-800 text-white placeholder:text-stone-600 focus-visible:ring-1 focus-visible:ring-[#F56A1F] focus-visible:border-[#F56A1F] text-center tracking-[0.4em] font-mono text-lg"
                  />
                </div>
              </div>

              {error && (
                <div
                  data-testid="login-pin-error"
                  className="flex items-start gap-2 p-3 rounded-lg bg-red-950/40 border border-red-900/50 text-red-300 text-sm">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                data-testid="login-pin-submit"
                type="submit"
                disabled={loading || pin.length < 4}
                className="w-full h-11 bg-[#F56A1F] hover:bg-[#E05206] text-white font-medium gap-2 shadow-lg shadow-[#F56A1F]/20">
                {loading ? "Verifica in corso…" : (<><LogIn className="w-4 h-4" /> Conferma PIN</>)}
              </Button>

              <button
                type="button"
                onClick={backToPassword}
                className="w-full flex items-center justify-center gap-1.5 text-[11px] text-stone-500 hover:text-stone-300 py-1 transition-colors">
                <ArrowLeft className="w-3 h-3" /> Rifai il login
              </button>
            </form>
            )}
          </Card>

          <div className="text-center mt-6 text-[11px] text-stone-600 tracking-wider">
            © 1964 IROLLO · Illumina & Arreda
          </div>
          <div
            data-testid="login-api-info"
            className="text-center mt-2 text-[10px] text-stone-700 font-mono tracking-tight break-all">
            API: {API_BASE}
          </div>
        </div>
      </div>
    </div>
  );
}
